import { Link } from 'react-router-dom';
import { useAuth, PlanType } from '@/context/AuthContext';

const plans = [
  {
    name: 'Free',
    price: '0€',
    period: '/mes',
    desc: 'Para empezar a explorar',
    accent: '#a8b2c1',
    features: [
      'Estadísticas básicas de equipos',
      'Foro de comunidad (lectura)',
      'Ranking de tipsters (top 10)',
      'Comparador de cuotas (3 casas)',
      'Ligas públicas (como espectador)',
    ],
    locked: [
      'Buscador de tendencias avanzado',
      'Alertas Dropping Odds',
      'Gestor de bankroll personal',
      'Filtros cruzados avanzados',
    ],
    cta: 'Empezar Gratis',
    ctaLink: '/estadisticas',
    highlight: false,
  },
  {
    name: 'Premium',
    price: '2,50€',
    period: '/mes',
    desc: 'Para apostadores serios',
    accent: '#00e87a',
    features: [
      'Todo lo de Free',
      'Buscador de tendencias avanzado',
      'Alertas Dropping Odds (20/día)',
      'Gestor de bankroll personal',
      'Filtros cruzados sin límite',
      'Comparador completo (6 casas)',
      'Participar en ligas competitivas',
      'Foro con publicación de picks',
    ],
    locked: [
      'Alertas SMS ilimitadas',
    ],
    cta: 'Activar Premium',
    ctaLink: '/estadisticas',
    highlight: true,
  },
  {
    name: 'Elite',
    price: '5,00€',
    period: '/mes',
    desc: 'Para profesionales del dato',
    accent: '#ffd700',
    features: [
      'Todo lo de Premium',
      'Alertas Dropping Odds ilimitadas',
      'Alertas SMS en tiempo real',
      'Ligas privadas sin límite de jugadores',
      'Badge Elite verificado en perfil',
      'Acceso a datos históricos (5 años)',
      'Soporte prioritario 24/7',
    ],
    locked: [],
    cta: 'Activar Elite',
    ctaLink: '/comunidad',
    highlight: false,
  },
];

export default function PricingSection() {
  const { isRegistered, isPremium, plan, openRegisterModal, openPaywallModal } = useAuth();

  const handlePlanCta = (planId: string) => {
    if (planId === 'free') {
      if (!isRegistered) {
        openRegisterModal();
      }
    } else {
      if (!isRegistered) {
        openRegisterModal();
      } else {
        openPaywallModal(planId as 'premium' | 'elite');
      }
    }
  };

  const isCurrentPlan = (planName: string) => {
    return isRegistered && plan === planName.toLowerCase();
  };

  return (
    <section className="py-24 bg-[#0d0f14]">
      <div className="max-w-[1440px] mx-auto px-6">
        <div className="text-center mb-14">
          <p className="text-[#00e87a] text-xs font-bold tracking-widest uppercase mb-3">Planes y Precios</p>
          <h2 className="text-white text-4xl md:text-5xl font-black uppercase">
            ELIGE TU NIVEL<br />
            <span className="text-white/50">DE VENTAJA</span>
          </h2>
          <p className="text-[#a8b2c1] text-base mt-4 max-w-lg mx-auto">
            Empieza gratis. Escala cuando lo necesites. Sin permanencia.
          </p>
          {/* Coming soon banner */}
          <div className="inline-flex items-center gap-2.5 mt-5 px-5 py-2.5 bg-[#ffd700]/10 border border-[#ffd700]/30 rounded-full">
            <i className="ri-time-line text-[#ffd700] text-sm"></i>
            <span className="text-[#ffd700] text-sm font-bold">Los planes de pago estarán disponibles próximamente</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((p) => (
            <div
              key={p.name}
              className={`relative bg-[#151719] rounded-2xl p-8 border transition-all ${
                p.highlight
                  ? 'border-[#00e87a] scale-105 shadow-2xl shadow-[#00e87a]/10'
                  : 'border-[#2a2d3a] hover:border-white/20'
              }`}
            >
              {p.highlight && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-5 py-1.5 bg-[#00e87a] rounded-full">
                  <span className="text-black text-xs font-black uppercase tracking-wide">Más Popular</span>
                </div>
              )}

              {isCurrentPlan(p.name) && (
                <div className="absolute top-4 right-4 px-2.5 py-0.5 bg-[#00e87a]/15 border border-[#00e87a]/30 rounded-full">
                  <span className="text-[#00e87a] text-xs font-bold uppercase">Plan Actual</span>
                </div>
              )}

              <div className="mb-6">
                <span className="text-xs font-bold uppercase tracking-widest"
                  style={{ color: p.accent }}>{p.name}</span>
                <div className="flex items-end gap-1 mt-2">
                  <span className="text-white text-5xl font-black">{p.price}</span>
                  <span className="text-[#6b7280] text-base mb-2">{p.period}</span>
                </div>
                <p className="text-[#6b7280] text-sm mt-1">{p.desc}</p>
              </div>

              <ul className="space-y-3 mb-6">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-[#a8b2c1]">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0"
                      style={{ color: p.accent }}>
                      <i className="ri-check-line text-sm"></i>
                    </div>
                    {f}
                  </li>
                ))}
                {p.locked.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-[#3d4149]">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-lock-line text-sm"></i>
                    </div>
                    {f}
                  </li>
                ))}
              </ul>

              {isCurrentPlan(p.name) ? (
                <div className="block w-full py-3.5 rounded-xl text-center text-sm font-bold uppercase tracking-wide border border-[#00e87a]/30 text-[#00e87a]/60 cursor-default whitespace-nowrap">
                  Tu plan actual
                </div>
              ) : p.name.toLowerCase() === 'free' ? (
                <button
                  onClick={() => handlePlanCta(p.name.toLowerCase())}
                  className="block w-full py-3.5 rounded-xl text-center text-sm font-bold uppercase tracking-wide transition-all cursor-pointer whitespace-nowrap border border-[#2a2d3a] text-white hover:bg-white/5"
                >
                  {p.cta}
                </button>
              ) : (
                <div className="block w-full py-3.5 rounded-xl text-center text-sm font-bold uppercase tracking-wide border border-dashed border-[#2a2d3a] text-[#6b7280] cursor-default whitespace-nowrap flex items-center justify-center gap-2">
                  <i className="ri-time-line text-sm"></i>
                  Próximamente
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
